import type { PropsWithChildren } from "react";

import { useForm } from "react-hook-form";

import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  Select,
  SelectContent,
  SelectItem,
  SelectItemDescription,
  SelectTrigger,
  SelectValue,
} from "@alaffia-technology-solutions/ds";

import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

const editReasonFormSchema = z.object({
  reason: z.string().optional(),
});

type EditReasonFormData = z.infer<typeof editReasonFormSchema>;

interface EditReasonFormProps extends PropsWithChildren {
  onCancel: () => void;
  onSubmit: (data: EditReasonFormData) => void;
  isEdited?: boolean;
  defaultReason?: string | null;
  isLoading?: boolean;
}

const changeReasons = [
  {
    label: "Verbiage update",
    description: "The content was accurate but I want to update the verbiage",
  },
  {
    label: "Incomplete content",
    description:
      "The content was incomplete and was missing information from the source",
  },
  {
    label: "Inaccurate content",
    description:
      "The content was inaccurate and was documented differently in the source",
  },
  {
    label: "Content not found",
    description: "The content was not found in the source",
  },
];

export function EditReasonForm({
  onCancel,
  onSubmit,
  isEdited = false,
  isLoading = false,
  defaultReason,
  children,
}: EditReasonFormProps) {
  const form = useForm<EditReasonFormData>({
    resolver: zodResolver(editReasonFormSchema),
    defaultValues: {
      reason: defaultReason ?? undefined,
    },
  });

  const {
    formState: { isValid, isDirty },
  } = form;

  const saveDisabled = (!isDirty && !isEdited) || !isValid || isLoading;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="af-w-full">
        <div className="af-flex af-flex-col af-gap-3">
          <div className="af-flex af-flex-row af-items-center af-gap-8">
            <div className="af-flex-1">
              <FormField
                control={form.control}
                name="reason"
                render={({ field }) => (
                  <FormItem>
                    <Select
                      label="Edit Reason"
                      onValueChange={field.onChange}
                      value={field.value ?? ""}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select reason" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {changeReasons.map(({ label, description }) => (
                          <SelectItem key={label} value={label}>
                            {label}
                            <SelectItemDescription>
                              {description}
                            </SelectItemDescription>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            </div>

            <div className="af-flex af-flex-row af-items-center af-gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saveDisabled}>
                Save
              </Button>
            </div>
          </div>
          {children}
        </div>
      </form>
    </Form>
  );
}
